import { useState, useEffect, useRef, createContext, useContext, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../../store/authStore'
import PermissionGuard from '../auth/PermissionGuard'
import { ChevronDown, ChevronRight, X, Lock } from 'lucide-react'
import {
  getSidebarModules,
  getGroupedModules,
  groupModulesByNavGroup,
  MARKETPLACES,
  type MarketplaceType,
  type ModuleConfig,
  filterModulesByMarketplace,
  filterModulesByUserAccess,
} from '../../config/moduleRegistry'
import { hasVideosForLocale } from '../../config/videoRegistry'
import { usePermissions } from '../../hooks/shared/auth'
import { useFeatureFlags } from '../../hooks/shared/useFeatureFlags'
import { useModuleFlagGate } from '../../hooks/shared/useModuleFlagGate'
import logoIcon from '../../assets/logo-icon.png'
import { AppIcon } from '../icons/AppIcon'
import type { AppIconName } from '../../config/appIcons'
import { SidebarFeedbackButton } from '../feedback/SidebarFeedbackButton'
import { useFeedback } from '../feedback/FeedbackContext'
import { OnboardingLockedTooltip } from '../onboarding/OnboardingLockedTooltip'
import { useOnboardingSidebarGating } from '../../hooks/shared/onboarding/useOnboardingSidebarGating'
import {
  reviewCountForModule,
  useSidebarReviewCounts,
} from '../../hooks/shared/useSidebarReviewCounts'
import {
  FALLBACK_DISCORD_INVITE_URL,
  useDiscordInvite,
} from '../../hooks/shared/useDiscordInvite'

export interface SidebarProps {
  isCollapsed: boolean
  onToggleCollapse: () => void
  isMobileOpen: boolean
  onCloseMobile: () => void
}

// ─── Context ──────────────────────────────────────────────────────────────────

/**
 * When true, NavItems inside a flyout render expanded (icon + label)
 * regardless of the sidebar collapsed state.
 */
const FlyoutContext = createContext(false)

// ─── Shared types ─────────────────────────────────────────────────────────────

interface FixedPos {
  top: number
  left: number
}

// ─── Shared hooks ─────────────────────────────────────────────────────────────

/**
 * Manages tooltip position + visibility for any element ref.
 * Positions tooltip to the right of the element via fixed coordinates,
 * escaping any overflow:hidden/auto ancestor.
 */
function usePortalTooltip<T extends HTMLElement = HTMLElement>() {
  const ref = useRef<T>(null)
  const [pos, setPos] = useState<FixedPos | null>(null)
  const [visible, setVisible] = useState(false)

  const show = useCallback(() => {
    if (ref.current) {
      const r = ref.current.getBoundingClientRect()
      setPos({ top: r.top + r.height / 2, left: r.right + 8 })
    }
    setVisible(true)
  }, [])

  const hide = useCallback(() => setVisible(false), [])

  return { ref, pos, visible, show, hide }
}

/**
 * Manages flyout open/close state, debounced close, and position anchoring.
 * Used by SidebarGroup to show a submenu flyout when the sidebar is collapsed.
 */
function useFlyout() {
  const [visible, setVisible] = useState(false)
  const [pos, setPos] = useState<FixedPos | null>(null)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout>>()

  const open = useCallback(() => {
    if (anchorRef.current) {
      const r = anchorRef.current.getBoundingClientRect()
      setPos({ top: r.top, left: r.right + 4 })
    }
    clearTimeout(closeTimer.current)
    setVisible(true)
  }, [])

  const close = useCallback(() => {
    closeTimer.current = setTimeout(() => setVisible(false), 150)
  }, [])

  const hide = useCallback(() => setVisible(false), [])

  useEffect(() => () => clearTimeout(closeTimer.current), [])

  return { visible, pos, anchorRef, open, close, hide }
}

// ─── Shared primitives ────────────────────────────────────────────────────────

/**
 * Tooltip portal — rendered at body level via position:fixed so it is never
 * clipped by an overflow:hidden/auto ancestor (e.g. the scrollable nav).
 */
function PortalTooltip({ label, pos, visible }: { label: string; pos: FixedPos | null; visible: boolean }) {
  if (!visible || !pos) return null
  return createPortal(
    <span
      style={{ position: 'fixed', top: pos.top, left: pos.left, transform: 'translateY(-50%)' }}
      className="pointer-events-none z-[100] whitespace-nowrap rounded bg-[#212121] border border-[#171717] px-2 py-1 text-xs text-gray-300 shadow-lg"
    >
      {label}
    </span>,
    document.body,
  )
}

function readSidebarGroupOpen(persistKey: string | undefined, defaultOpen: boolean) {
  if (!persistKey) return defaultOpen
  try {
    const stored = localStorage.getItem(`sidebar-group-${persistKey}`)
    if (stored === '0') return false
    if (stored === '1') return true
  } catch {
    // ignore
  }
  return defaultOpen
}

/** Section heading label — hidden in desktop icon-rail mode (still shown in flyouts). */
function SidebarSectionLabel({ label, isCollapsed }: { label: string; isCollapsed: boolean }) {
  const inFlyout = useContext(FlyoutContext)
  const collapsed = inFlyout ? false : isCollapsed
  return (
    <div
      className={`text-xs text-gray-400 uppercase tracking-wider mb-2 px-3 ${
        collapsed ? 'lg:hidden' : ''
      }`}
    >
      {label}
    </div>
  )
}

/**
 * Expandable nav subsection inside a marketplace group (e.g. Poprawianie ofert).
 * No own flyout — lives inside the parent group / its flyout.
 * In icon-rail mode (collapsed, not in flyout) chrome is hidden and children stay visible.
 */
function SidebarNavSection({
  title,
  persistKey,
  matchPaths,
  defaultOpen = true,
  isCollapsed = false,
  children,
}: {
  title: string
  persistKey: string
  matchPaths: string[]
  defaultOpen?: boolean
  isCollapsed?: boolean
  children: React.ReactNode
}) {
  const location = useLocation()
  const inFlyout = useContext(FlyoutContext)
  const hideChrome = !inFlyout && isCollapsed
  const pathMatches = matchPaths.some(
    (prefix) =>
      location.pathname === prefix || location.pathname.startsWith(`${prefix}/`),
  )
  const [isOpen, setIsOpen] = useState(() =>
    readSidebarGroupOpen(persistKey, defaultOpen || pathMatches),
  )

  useEffect(() => {
    if (pathMatches) setIsOpen(true)
  }, [pathMatches])

  const persistOpen = (next: boolean) => {
    try {
      localStorage.setItem(`sidebar-group-${persistKey}`, next ? '1' : '0')
    } catch {
      // ignore
    }
  }

  const showChildren = isOpen || hideChrome

  return (
    <div className="mb-1">
      {!hideChrome && (
        <button
          type="button"
          onClick={() => {
            setIsOpen((prev) => {
              const next = !prev
              persistOpen(next)
              return next
            })
          }}
          className="w-full flex items-center justify-between gap-2 rounded px-3 py-1.5 text-xs text-gray-400 uppercase tracking-wider hover:bg-[#1f1f1f] hover:text-gray-200 transition"
        >
          <span className="min-w-0 text-left whitespace-normal break-words leading-snug">{title}</span>
          {isOpen ? <ChevronDown className="w-3.5 h-3.5 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 shrink-0" />}
        </button>
      )}
      {showChildren && <div className="mt-0.5 space-y-1">{children}</div>}
    </div>
  )
}

/** Orange circle: flows waiting in Przegląd for this module. */
function ReviewCountBadge({ count }: { count?: number }) {
  if (!count || count <= 0) return null
  const label = count > 99 ? '99+' : String(count)
  return (
    <span
      className="flex h-5 min-w-[1.25rem] flex-shrink-0 items-center justify-center rounded-full bg-orange-600 px-1.5 text-[10px] font-bold leading-none text-white"
      title={`${count}`}
    >
      {label}
    </span>
  )
}

/** Compact beta pill — flex sibling so it centers with the row. */
function BetaModuleBadge({ label }: { label: string }) {
  return (
    <span className="inline-flex h-5 shrink-0 items-center rounded px-1.5 text-[9px] font-bold uppercase leading-none tracking-wide bg-amber-600 text-white">
      {label}
    </span>
  )
}

// ─── Nav item ─────────────────────────────────────────────────────────────────

interface NavItemProps {
  to: string
  icon: AppIconName
  label: string
  isActive: boolean
  isCollapsed: boolean
  reviewCount?: number
  showBetaBadge?: boolean
  betaBadgeLabel?: string
}

function SidebarNavItem({
  to,
  icon,
  label,
  isActive,
  isCollapsed,
  reviewCount,
  showBetaBadge = false,
  betaBadgeLabel = 'Beta',
}: NavItemProps) {
  const inFlyout = useContext(FlyoutContext)
  const collapsed = inFlyout ? false : isCollapsed
  const { ref, pos, visible, show, hide } = usePortalTooltip<HTMLAnchorElement>()
  const showCollapsedDot = (reviewCount ?? 0) > 0 || showBetaBadge

  const activeClass = isActive
    ? inFlyout
      ? 'bg-[#333333] text-white'
      : 'bg-[#212121] text-white'
    : 'text-gray-300 hover:bg-[#1f1f1f] hover:text-white'

  return (
    <>
      <Link
        ref={ref}
        to={to}
        onMouseEnter={collapsed ? show : undefined}
        onMouseLeave={collapsed ? hide : undefined}
        className={`relative flex items-center gap-2 rounded px-3 py-2 text-sm transition ${activeClass} ${
          collapsed ? 'lg:px-0 lg:justify-center' : ''
        }`}
      >
        <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center">
          <AppIcon
            name={icon}
            size={20}
            weight={isActive ? 'fill' : 'regular'}
            className={isActive ? 'text-white' : 'text-current'}
          />
        </span>
        <span
          className={`min-w-0 flex-1 font-switzer leading-5 whitespace-normal break-words ${
            collapsed ? 'lg:hidden' : ''
          }`}
        >
          {label}
        </span>
        {!collapsed && showBetaBadge && <BetaModuleBadge label={betaBadgeLabel} />}
        {!collapsed && <ReviewCountBadge count={reviewCount} />}
        {collapsed && !inFlyout && showCollapsedDot && (
          <span className="absolute right-1 top-1 hidden h-2 w-2 rounded-full bg-orange-500 lg:block" />
        )}
      </Link>
      {collapsed && !inFlyout && <PortalTooltip label={label} pos={pos} visible={visible} />}
    </>
  )
}

// ─── Module item (handles core vs permission-guarded) ─────────────────────────

interface ModuleItemProps {
  module: ModuleConfig
  isActive: boolean
  isCollapsed: boolean
  label: string
  locked?: boolean
  lockTooltip?: string | null
  reviewCount?: number
  betaBadgeLabel?: string
}

/**
 * Renders a SidebarNavItem for a module config.
 * Core modules are rendered directly; others are wrapped with PermissionGuard.
 */
function SidebarModuleItem({
  module,
  isActive,
  isCollapsed,
  label,
  locked = false,
  lockTooltip = null,
  reviewCount = 0,
  betaBadgeLabel = 'Beta',
}: ModuleItemProps) {
  const inFlyout = useContext(FlyoutContext)
  const collapsed = inFlyout ? false : isCollapsed
  const showBetaBadge = module.status === 'beta'

  if (locked) {
    const lockedItem = (
      <OnboardingLockedTooltip label={lockTooltip} className="w-full">
        <div
          className={`relative flex items-center gap-2 rounded px-3 py-2 text-sm text-gray-500 opacity-70 cursor-not-allowed ${
            collapsed ? 'lg:px-0 lg:justify-center' : ''
          }`}
        >
          <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center">
            <AppIcon name={module.icon} size={20} className="text-gray-500" />
          </span>
          <span
            className={`min-w-0 flex-1 font-switzer leading-5 whitespace-normal break-words ${
              collapsed ? 'lg:hidden' : ''
            }`}
          >
            {label}
          </span>
          {!collapsed && showBetaBadge && <BetaModuleBadge label={betaBadgeLabel} />}
          {!collapsed && <Lock className="h-3.5 w-3.5 shrink-0 text-gray-500" />}
        </div>
      </OnboardingLockedTooltip>
    )
    if (module.isCore || module.alwaysVisibleInNav) return lockedItem
    return <PermissionGuard module={module.id}>{lockedItem}</PermissionGuard>
  }

  const item = (
    <SidebarNavItem
      to={module.route}
      icon={module.icon}
      label={label}
      isActive={isActive}
      isCollapsed={isCollapsed}
      reviewCount={reviewCount}
      showBetaBadge={showBetaBadge}
      betaBadgeLabel={betaBadgeLabel}
    />
  )
  if (module.isCore || module.alwaysVisibleInNav) return item
  return <PermissionGuard module={module.id}>{item}</PermissionGuard>
}

// ─── Marketplace badge ────────────────────────────────────────────────────────

function MarketplaceBadge({ color, letter }: { color: string; letter: string }) {
  return (
    <span
      className="flex-shrink-0 w-5 h-5 rounded flex items-center justify-center text-white text-[10px] font-bold leading-none"
      style={{ backgroundColor: color }}
    >
      {letter}
    </span>
  )
}

// ─── Group (collapsible section with flyout in icon-rail mode) ────────────────

interface SidebarGroupProps {
  title: string
  icon?: React.ReactNode
  children: React.ReactNode
  defaultOpen?: boolean
  isCollapsed: boolean
  onExpand: () => void
  /** Persist expanded/collapsed state in localStorage. */
  persistKey?: string
  /** Auto-expand when current path matches any prefix (e.g. `/admin`). */
  matchPaths?: string[]
}

function SidebarGroup({
  title,
  icon,
  children,
  defaultOpen = false,
  isCollapsed,
  onExpand,
  persistKey,
  matchPaths,
}: SidebarGroupProps) {
  const location = useLocation()
  const pathMatches =
    matchPaths?.some(
      (prefix) =>
        location.pathname === prefix || location.pathname.startsWith(`${prefix}/`),
    ) ?? false

  const [isOpen, setIsOpen] = useState(() => readSidebarGroupOpen(persistKey, defaultOpen || pathMatches))
  const { visible: flyoutVisible, pos: flyoutPos, anchorRef, open: openFlyout, close: closeFlyout, hide: hideFlyout } =
    useFlyout()

  useEffect(() => {
    if (!isCollapsed) hideFlyout()
  }, [isCollapsed, hideFlyout])

  useEffect(() => {
    if (pathMatches) setIsOpen(true)
  }, [pathMatches])

  const persistOpen = (next: boolean) => {
    if (!persistKey) return
    try {
      localStorage.setItem(`sidebar-group-${persistKey}`, next ? '1' : '0')
    } catch {
      // ignore
    }
  }

  const handleClick = () => {
    if (isCollapsed) {
      onExpand()
      setIsOpen(true)
      persistOpen(true)
    } else {
      setIsOpen((prev) => {
        const next = !prev
        persistOpen(next)
        return next
      })
    }
  }

  return (
    <div className="mb-2">
      <button
        ref={anchorRef}
        onClick={handleClick}
        onMouseEnter={isCollapsed ? openFlyout : undefined}
        onMouseLeave={isCollapsed ? closeFlyout : undefined}
        className={`w-full flex items-center rounded py-2 text-sm text-gray-300 hover:bg-[#1f1f1f] hover:text-white transition ${
          isCollapsed ? 'px-3 lg:px-0 lg:justify-center' : 'justify-between px-3'
        }`}
      >
        <div className="flex items-center space-x-2">
          {icon && (
            <span className="flex-shrink-0 flex items-center justify-center">
              {icon}
            </span>
          )}
          <span className={`font-medium ${isCollapsed ? 'lg:hidden' : ''}`}>{title}</span>
        </div>
        <span className={isCollapsed ? 'lg:hidden' : ''}>
          {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </span>
      </button>

      {/* Flyout portal — position:fixed escapes the nav's overflow context */}
      {isCollapsed && flyoutVisible && flyoutPos &&
        createPortal(
          <div
            style={{ position: 'fixed', top: flyoutPos.top, left: flyoutPos.left }}
            className="z-[100] hidden lg:block"
            onMouseEnter={openFlyout}
            onMouseLeave={closeFlyout}
          >
            <div className="min-w-[180px] rounded-lg bg-[#212121] border border-[#171717] shadow-2xl py-2">
              <div className="px-3 py-1.5 text-xs font-semibold text-white uppercase tracking-wider border-b border-[#171717] mb-1">
                {title}
              </div>
              <FlyoutContext.Provider value={true}>
                <div className="px-1 space-y-0.5">{children}</div>
              </FlyoutContext.Provider>
            </div>
          </div>,
          document.body,
        )}

      {/* Inline subitems — expanded sidebar */}
      {!isCollapsed && isOpen && <div className="ml-2 mt-1 space-y-1">{children}</div>}
    </div>
  )
}

// ─── Discord link ─────────────────────────────────────────────────────────────

function DiscordIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057c.002.022.015.043.03.056a19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
    </svg>
  )
}

function SidebarDiscordLink({ isCollapsed }: { isCollapsed: boolean }) {
  const { t } = useTranslation('dashboard')
  const { ref, pos, visible, show, hide } = usePortalTooltip<HTMLAnchorElement>()
  const { data } = useDiscordInvite()
  const href = data?.url || FALLBACK_DISCORD_INVITE_URL

  return (
    <div className={`border-t border-[#171717] ${isCollapsed ? 'px-1 py-1.5' : 'px-3 py-1.5'}`}>
      <a
        ref={ref}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onMouseEnter={isCollapsed ? show : undefined}
        onMouseLeave={isCollapsed ? hide : undefined}
        className={`flex items-center gap-2.5 rounded px-2.5 py-2 text-sm text-gray-400 hover:bg-[#1f1f1f] hover:text-white transition ${
          isCollapsed ? 'lg:justify-center' : ''
        }`}
      >
        <DiscordIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <span className={`font-switzer truncate ${isCollapsed ? 'lg:hidden' : ''}`}>
          {t('sidebar.community.discord')}
        </span>
      </a>
      {isCollapsed && (
        <PortalTooltip label={t('sidebar.community.discord')} pos={pos} visible={visible} />
      )}
    </div>
  )
}

// ─── Feedback (bottom of sidebar) ─────────────────────────────────────────────

function SidebarFeedback({ isCollapsed }: { isCollapsed: boolean }) {
  const { t } = useTranslation('dashboard')
  const { isOpen } = useFeedback()
  const { ref, pos, visible, show, hide } = usePortalTooltip<HTMLButtonElement>()

  return (
    <div className="relative flex-shrink-0">
      <SidebarFeedbackButton
        isCollapsed={isCollapsed}
        buttonRef={ref}
        onMouseEnter={isCollapsed && !isOpen ? show : undefined}
        onMouseLeave={isCollapsed && !isOpen ? hide : undefined}
      />
      {isCollapsed && !isOpen && (
        <PortalTooltip label={t('sidebar.feedback.label')} pos={pos} visible={visible} />
      )}
    </div>
  )
}

// ─── Main Sidebar ─────────────────────────────────────────────────────────────

/**
 * Sidebar — dynamically generated from moduleRegistry.
 *
 * RWD behaviour:
 *  - Mobile (<lg): fixed overlay drawer, hidden until `isMobileOpen`
 *  - Desktop (lg+): relative in-flow panel, collapsible to icon rail (w-16)
 *
 * SOLID:
 *  - Single Responsibility: navigation UI only
 *  - Open/Closed: new modules added to registry, sidebar auto-updates
 *  - Dependency Inversion: depends on moduleRegistry abstraction
 */
export default function Sidebar({
  isCollapsed,
  onToggleCollapse,
  isMobileOpen,
  onCloseMobile,
}: SidebarProps) {
  const location = useLocation()
  const { user } = useAuthStore()
  const { t, i18n } = useTranslation('dashboard')
  const {
    isMarketplaceEnabled,
    isAIConfigEnabled,
    isUsersAnalyticsEnabled,
    isAIUsageEnabled,
    isLoading,
  } = useFeatureFlags()
  const { hasPermission } = usePermissions()
  const { isModuleFlagOpen } = useModuleFlagGate()
  const { getModuleLock } = useOnboardingSidebarGating()
  const { data: reviewCounts } = useSidebarReviewCounts(!!user)
  const schoolVideosAvailable = hasVideosForLocale(i18n.language)

  useEffect(() => {
    onCloseMobile()
  }, [location.pathname, onCloseMobile])

  const allSidebarModules = getSidebarModules()
  const sidebarModules = filterModulesByUserAccess(
    filterModulesByMarketplace(allSidebarModules, isMarketplaceEnabled).filter(
      (module) =>
        isModuleFlagOpen(module) &&
        !(module.id === 'zuzycie_ai' && !isAIUsageEnabled()) &&
        !(module.id === 'szkola_ofertatora' && !schoolVideosAvailable),
    ),
    hasPermission,
  )
  const groupedModules = getGroupedModules(sidebarModules)
  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`)

  const sortedMarketplaces = Array.from(groupedModules.entries()).sort(
    (a, b) => MARKETPLACES[a[0]].order - MARKETPLACES[b[0]].order,
  )
  const visibleMarketplaces = sortedMarketplaces.filter(
    ([marketplace]) => marketplace !== 'general',
  )
  /** Single marketplace → skip marketplace accordion (e.g. Allegro-only users). */
  const hideMarketplaceChrome = visibleMarketplaces.length === 1

  return (
    <aside
      className={[
        'flex-shrink-0 bg-[#0d0d0d] text-white flex flex-col h-full overflow-hidden',
        'fixed inset-y-0 left-0 z-50 lg:relative lg:inset-auto lg:z-auto',
        'transition-all duration-200 ease-in-out',
        isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        isCollapsed ? 'w-64 lg:w-16' : 'w-64',
      ].join(' ')}
    >
      {/* Header: logo + mobile close */}
      <div
        className={`flex items-center border-b border-[#171717] h-16 flex-shrink-0 ${
          isCollapsed ? 'lg:justify-center px-2' : 'px-4'
        }`}
      >
        <Link
          to="/dashboard"
          className={`flex items-center gap-2.5 flex-1 min-w-0 ${isCollapsed ? 'lg:justify-center' : ''}`}
        >
          <img src={logoIcon} alt="Ofertator" className="flex-shrink-0 h-8 w-8 object-contain" />
          <span
            className={`font-switzer font-bold text-xl text-white tracking-tight whitespace-nowrap ${
              isCollapsed ? 'lg:hidden' : ''
            }`}
          >
            Ofertator
          </span>
        </Link>

        {/* Mobile close button only — desktop toggle lives in topbar */}
        <button
          onClick={onCloseMobile}
          aria-label="Close sidebar"
          className="lg:hidden flex items-center justify-center w-7 h-7 rounded text-gray-400 hover:text-white hover:bg-gray-700 transition flex-shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Nav — overflow-x-hidden prevents absolute children from causing horizontal scroll */}
      <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto overflow-x-hidden sidebar-scrollbar">
        {isLoading ? (
          <div className="space-y-4 animate-pulse px-2">
            <div className="h-8 bg-gray-700 rounded" />
            <div className="h-8 bg-gray-700 rounded" />
            <div className="h-8 bg-gray-700 rounded" />
            <div className="space-y-2 pt-4">
              <div className="h-6 bg-gray-800 rounded w-3/4" />
              <div className="h-6 bg-gray-800 rounded w-2/3" />
            </div>
          </div>
        ) : (
          <>
            {/* Main section */}
            <div className="mb-3">
              <SidebarSectionLabel label={t('sidebar.main')} isCollapsed={isCollapsed} />
              <SidebarNavItem
                to="/dashboard"
                icon="house"
                label="Dashboard"
                isActive={isActive('/dashboard')}
                isCollapsed={isCollapsed}
              />
              {groupedModules.get('general')?.map(module => {
                const lock = getModuleLock(module.id)
                return (
                <SidebarModuleItem
                  key={module.id}
                  module={module}
                  isActive={isActive(module.route)}
                  isCollapsed={isCollapsed}
                  label={t(module.name)}
                  locked={lock.locked}
                  lockTooltip={lock.tooltip}
                />
              )})}
            </div>

            {/* Marketplace sections */}
            {visibleMarketplaces.map(([marketplace, modules]) => {
                const marketplaceConfig = MARKETPLACES[marketplace as MarketplaceType]
                const renderModule = (module: ModuleConfig) => {
                  const lock = getModuleLock(module.id)
                  return (
                    <SidebarModuleItem
                      key={module.id}
                      module={module}
                      isActive={isActive(module.route)}
                      isCollapsed={isCollapsed}
                      label={t(module.name)}
                      locked={lock.locked}
                      lockTooltip={lock.tooltip}
                      reviewCount={reviewCountForModule(reviewCounts, module.id)}
                      betaBadgeLabel={t('sidebar.moduleStatus.beta')}
                    />
                  )
                }
                const renderMarketplaceModules = () => {
                  const navBuckets = groupModulesByNavGroup(modules)
                  const hasNavGroups = navBuckets.some((b) => b.group !== null)
                  if (!hasNavGroups) {
                    return <>{modules.map(renderModule)}</>
                  }
                  return (
                    <>
                      {navBuckets.map((bucket) => {
                        if (!bucket.group) {
                          return (
                            <div key="ungrouped" className="space-y-1">
                              {bucket.modules.map(renderModule)}
                            </div>
                          )
                        }
                        return (
                          <SidebarNavSection
                            key={bucket.group.id}
                            title={t(bucket.group.labelKey)}
                            persistKey={`nav-${marketplace}-${bucket.group.id}`}
                            matchPaths={bucket.modules.map((m) => m.route)}
                            defaultOpen
                            isCollapsed={isCollapsed}
                          >
                            {bucket.modules.map(renderModule)}
                          </SidebarNavSection>
                        )
                      })}
                    </>
                  )
                }

                if (hideMarketplaceChrome) {
                  return (
                    <div key={marketplace} className="mb-3">
                      {renderMarketplaceModules()}
                    </div>
                  )
                }

                return (
                  <SidebarGroup
                    key={marketplace}
                    title={t(`marketplaces.${marketplace}`, { defaultValue: marketplaceConfig.name })}
                    icon={<MarketplaceBadge color={marketplaceConfig.color} letter={marketplaceConfig.letter} />}
                    defaultOpen={marketplace === 'allegro'}
                    isCollapsed={isCollapsed}
                    onExpand={onToggleCollapse}
                  >
                    {renderMarketplaceModules()}
                  </SidebarGroup>
                )
              })}

            {/* Analytics (admin only) */}
            {user?.role === 'admin' && (
              <div className="border-t border-[#171717] mt-3 pt-3">
                <SidebarGroup
                  title={t('sidebar.analytics')}
                  icon={<AppIcon name="chart-bar" size={20} className="text-current" />}
                  defaultOpen={false}
                  isCollapsed={isCollapsed}
                  onExpand={onToggleCollapse}
                  persistKey="analytics"
                  matchPaths={['/users-analytics', '/admin/onboarding-analytics', '/admin/grafikomat-feedback']}
                >
                  {isUsersAnalyticsEnabled() && (
                    <SidebarNavItem
                      to="/users-analytics"
                      icon="chart-bar"
                      label={t('sidebar.adminLinks.usersAnalytics')}
                      isActive={isActive('/users-analytics')}
                      isCollapsed={isCollapsed}
                    />
                  )}
                  <SidebarNavItem
                    to="/admin/onboarding-analytics"
                    icon="graduation-cap"
                    label={t('sidebar.adminLinks.onboardingAnalytics')}
                    isActive={isActive('/admin/onboarding-analytics')}
                    isCollapsed={isCollapsed}
                  />
                  <SidebarNavItem
                    to="/admin/grafikomat-feedback"
                    icon="image"
                    label={t('sidebar.adminLinks.grafikomatAnalytics')}
                    isActive={isActive('/admin/grafikomat-feedback')}
                    isCollapsed={isCollapsed}
                  />
                </SidebarGroup>
              </div>
            )}

            {/* Admin section */}
            {user?.role === 'admin' && (
              <div className="border-t border-[#171717] mt-3 pt-3">
                <SidebarGroup
                  title={t('sidebar.admin')}
                  icon={<AppIcon name="users-three" size={20} className="text-current" />}
                  defaultOpen={false}
                  isCollapsed={isCollapsed}
                  onExpand={onToggleCollapse}
                  persistKey="admin"
                  matchPaths={['/admin', '/dev/grafikomat-pipeline-preview']}
                >
                <SidebarNavItem
                  to="/admin/users"
                  icon="users-three"
                  label={t('sidebar.adminLinks.manageUsers')}
                  isActive={isActive('/admin/users')}
                  isCollapsed={isCollapsed}
                />
                <SidebarNavItem
                  to="/dev/grafikomat-pipeline-preview"
                  icon="image"
                  label="Graphics pipeline"
                  isActive={isActive('/dev/grafikomat-pipeline-preview')}
                  isCollapsed={isCollapsed}
                />
                {isAIConfigEnabled() && (
                  <SidebarNavItem
                    to="/admin/ai-prompts"
                    icon="robot"
                    label={t('sidebar.adminLinks.aiConfig')}
                    isActive={isActive('/admin/ai-prompts')}
                    isCollapsed={isCollapsed}
                  />
                )}
                {isAIConfigEnabled() && (
                  <SidebarNavItem
                    to="/admin/opisomat-config"
                    icon="file-text"
                    label={t('sidebar.adminLinks.opisomatConfig')}
                    isActive={isActive('/admin/opisomat-config')}
                    isCollapsed={isCollapsed}
                  />
                )}
                <SidebarNavItem
                  to="/admin/credits"
                  icon="coins"
                  label={t('credits:sidebar.adminCredits')}
                  isActive={isActive('/admin/credits')}
                  isCollapsed={isCollapsed}
                />
                <SidebarNavItem
                  to="/admin/referrals"
                  icon="gift"
                  label={t('sidebar.adminLinks.referrals')}
                  isActive={isActive('/admin/referrals')}
                  isCollapsed={isCollapsed}
                />
                <SidebarNavItem
                  to="/admin/accounting"
                  icon="credit-card"
                  label={t('sidebar.adminLinks.accounting')}
                  isActive={isActive('/admin/accounting')}
                  isCollapsed={isCollapsed}
                />
                <SidebarNavItem
                  to="/admin/site-notice"
                  icon="megaphone"
                  label={t('sidebar.adminLinks.siteNotice')}
                  isActive={isActive('/admin/site-notice')}
                  isCollapsed={isCollapsed}
                />
                <SidebarNavItem
                  to="/admin/ads-agency"
                  icon="megaphone"
                  label={t('sidebar.adminLinks.adsAgency')}
                  isActive={isActive('/admin/ads-agency')}
                  isCollapsed={isCollapsed}
                />
                <SidebarNavItem
                  to="/admin/feature-surveys"
                  icon="chart-bar"
                  label={t('sidebar.adminLinks.featureSurveys')}
                  isActive={isActive('/admin/feature-surveys')}
                  isCollapsed={isCollapsed}
                />
                </SidebarGroup>
              </div>
            )}
          </>
        )}
      </nav>

      <SidebarDiscordLink isCollapsed={isCollapsed} />
      <SidebarFeedback isCollapsed={isCollapsed} />
    </aside>
  )
}
